import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  HStack,
  Select,
  Spinner,
  Tag,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { Link2, Plus, X } from 'lucide-react'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import { taskService, type TaskDependencySummary } from '@/lib/services/taskService'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import TaskStatusPill from './TaskStatusPill'

type Props = {
  task: ChildTicketTask
  siblingTasks: ChildTicketTask[]
  isDisabled?: boolean
  onOpenTask?: (task: ChildTicketTask) => void
  onChanged?: () => void
}

export default function TaskDependencies({
  task,
  siblingTasks,
  isDisabled = false,
  onOpenTask,
  onChanged,
}: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [blockedBy, setBlockedBy] = useState<TaskDependencySummary[]>([])
  const [blocking, setBlocking] = useState<TaskDependencySummary[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<number | ''>('')
  const [removingId, setRemovingId] = useState<number | null>(null)

  const loadDependencies = useCallback(async () => {
    setLoading(true)
    try {
      const result = await taskService.listDependencies(task.id)
      setBlockedBy(result.blocked_by)
      setBlocking(result.blocking)
    } catch (err) {
      showError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [task.id, showError])

  useEffect(() => {
    void loadDependencies()
  }, [loadDependencies])

  const pickerOptions = useMemo(() => {
    const existingIds = new Set([
      task.id,
      ...blockedBy.map((item) => item.id),
      ...(task.depends_on_task_id ? [task.depends_on_task_id] : []),
    ])

    return siblingTasks.filter((sibling) => !existingIds.has(sibling.id))
  }, [blockedBy, siblingTasks, task.depends_on_task_id, task.id])

  const handleAdd = async () => {
    if (!selectedTaskId || isDisabled) return
    setAdding(true)
    try {
      await taskService.addDependency(task.id, selectedTaskId)
      showSuccess('Dependency added')
      setSelectedTaskId('')
      await loadDependencies()
      onChanged?.()
    } catch (err) {
      showError(err as Error)
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (dependsOnTaskId: number) => {
    if (isDisabled) return
    setRemovingId(dependsOnTaskId)
    try {
      await taskService.removeDependency(task.id, dependsOnTaskId)
      showSuccess('Dependency removed')
      await loadDependencies()
      onChanged?.()
    } catch (err) {
      showError(err as Error)
    } finally {
      setRemovingId(null)
    }
  }

  const renderChip = (item: TaskDependencySummary, removable: boolean) => {
    const sibling = siblingTasks.find((row) => row.id === item.id)
    const label = item.title || sibling?.title || item.task_number || `Task #${item.id}`

    return (
      <WrapItem key={item.id}>
        <Tag
          size="md"
          variant="subtle"
          colorScheme={item.is_complete ? 'green' : 'orange'}
          cursor={onOpenTask ? 'pointer' : 'default'}
          onClick={() => {
            if (onOpenTask && sibling) onOpenTask(sibling)
          }}
        >
          <HStack spacing={2}>
            <Link2 size={12} />
            <Text fontSize="xs">{label}</Text>
            {sibling?.status ? <TaskStatusPill status={sibling.status} /> : null}
            {removable ? (
              <Button
                size="xs"
                variant="ghost"
                minW="auto"
                h="auto"
                p={0}
                aria-label="Remove dependency"
                isLoading={removingId === item.id}
                isDisabled={isDisabled}
                onClick={(event) => {
                  event.stopPropagation()
                  void handleRemove(item.id)
                }}
              >
                <X size={12} />
              </Button>
            ) : null}
          </HStack>
        </Tag>
      </WrapItem>
    )
  }

  if (loading) {
    return (
      <HStack py={4} justify="center">
        <Spinner size="sm" />
        <Text fontSize="sm" color={colors.textSecondary}>
          Loading dependencies…
        </Text>
      </HStack>
    )
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontSize="sm" fontWeight="600" color={colors.textSecondary} mb={2}>
          Blocked by
        </Text>
        {blockedBy.length === 0 ? (
          <Text fontSize="sm" color={colors.textSecondary}>
            No upstream dependencies.
          </Text>
        ) : (
          <Wrap spacing={2}>{blockedBy.map((item) => renderChip(item, true))}</Wrap>
        )}
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="600" color={colors.textSecondary} mb={2}>
          Blocking
        </Text>
        {blocking.length === 0 ? (
          <Text fontSize="sm" color={colors.textSecondary}>
            No downstream tasks depend on this one.
          </Text>
        ) : (
          <Wrap spacing={2}>{blocking.map((item) => renderChip(item, false))}</Wrap>
        )}
      </Box>

      {!isDisabled && pickerOptions.length > 0 ? (
        <HStack align="flex-end" spacing={2}>
          <Box flex="1">
            <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>
              Add dependency
            </Text>
            <Select
              size="sm"
              placeholder="Select sibling task"
              value={selectedTaskId}
              onChange={(e) => setSelectedTaskId(e.target.value ? Number(e.target.value) : '')}
            >
              {pickerOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.title || option.task_number || `Task #${option.id}`}
                </option>
              ))}
            </Select>
          </Box>
          <Button
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => void handleAdd()}
            isLoading={adding}
            isDisabled={!selectedTaskId}
          >
            Add
          </Button>
        </HStack>
      ) : null}
    </VStack>
  )
}
